import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
// Entrada «pure»: la normal inyecta connect.js de Stripe al importarse, es decir, en cualquier página
// de la app; así solo se carga al montar ConnectPanel (pasarela con cuenta dada de alta).
import { loadConnectAndInitialize } from "@stripe/connect-js/pure";
import {
  ConnectAccountManagement, ConnectAccountOnboarding, ConnectComponentsProvider,
  ConnectNotificationBanner, ConnectPayments, ConnectPayouts,
} from "@stripe/react-connect-js";
import { api, formatError } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Cobros de la empresa con Stripe Connect: alta de la cuenta, avisos, gestión,
// pagos y transferencias, con los componentes integrados de Stripe. Sin pasarela,
// la instalación cobra con su propia clave y aquí solo se informa.
function ConnectPanel({ publishableKey, colorPrimary }) {
  const instance = useMemo(() => loadConnectAndInitialize({
    publishableKey,
    fetchClientSecret: async () => (await api.post("/admin/payments/session")).data.client_secret,
    locale: "es-ES",
    appearance: { overlays: "dialog", variables: { colorPrimary: colorPrimary || "#1E3F20" } },
  }), [publishableKey, colorPrimary]);
  return (
    <ConnectComponentsProvider connectInstance={instance}>
      <div className="space-y-6">
        <ConnectNotificationBanner />
        <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Alta y datos de la cuenta</h2>
          <ConnectAccountOnboarding onExit={() => window.location.reload()} />
        </section>
        <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Pagos</h2>
          <ConnectPayments />
        </section>
        <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Transferencias a tu banco</h2>
          <ConnectPayouts />
        </section>
        <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Cuenta</h2>
          <ConnectAccountManagement />
        </section>
      </div>
    </ConnectComponentsProvider>
  );
}

export default function Payments() {
  const brand = useBrand();
  const [estado, setEstado] = useState(null);
  const [publishableKey, setPublishableKey] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments/status", { params: { force: true } });
      setEstado(data);
      if (data.mode === "gateway" && data.account) {
        const s = await api.post("/admin/payments/session");
        setPublishableKey(s.data.publishable_key);
      }
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const alta = async () => {
    setBusy(true);
    try {
      await api.post("/admin/payments/account");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
        </div>
      </header>
      <main className="container-x py-8 max-w-5xl space-y-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Cobros</h1>
        {estado?.mode === "direct" && (
          <p className="text-[#5C5C5C]" data-testid="payments-direct">Esta instalación cobra con su propia cuenta de Stripe.</p>
        )}
        {estado?.mode === "demo" && (
          <p className="text-[#5C5C5C]" data-testid="payments-demo">Esto es una demostración: no se cobra ni se da de alta ninguna cuenta de cobros.</p>
        )}
        {estado?.mode === "gateway" && (
          <div className={`rounded-xl border p-4 text-sm ${estado.ready ? "border-marca/30 bg-marca/5 text-marca" : "border-[#E89B33]/40 bg-[#E89B33]/10 text-[#7A4E0E]"}`} data-testid="payments-status">
            {estado.ready
              ? "Los cobros con tarjeta están activos: la reserva online puede cobrar."
              : estado.account
                ? "Falta completar el alta de tu cuenta de cobros. Hasta entonces la reserva online no cobra."
                : "Da de alta tu cuenta de cobros para poder vender online."}
            {estado.error && <span className="block mt-1">{estado.error}</span>}
          </div>
        )}
        {estado?.mode === "gateway" && !estado.account && (
          <Button onClick={alta} disabled={busy} className="bg-marca hover:bg-marca-hover text-white" data-testid="payments-create">
            {busy ? "Creando…" : "Dar de alta la cuenta de cobros"}
          </Button>
        )}
        {publishableKey && <ConnectPanel publishableKey={publishableKey} colorPrimary={brand?.color_primary} />}
      </main>
    </div>
  );
}
